import type { Grain, TileColor, TileData, TileDefinition, TileSeries, TileUnit, Unternehmen } from "@kundeninfo/api-client";
import { hausfarbeImDiagramm, paletteFarbe, toneFarbe } from "@kundeninfo/ui/diagramm";
import type { DiagrammThema } from "@kundeninfo/ui/diagramm";
import { formatDatum } from "../../format.js";
import { dayMs, isoWeek, nowOnAxis } from "../tickets/timelineModel.js";
import { rangeText } from "./cockpitPeriod.js";
import { COCKPIT_TEXT_WEB as T } from "./cockpitTexts.js";

/**
 * ZEICHENREGELN UND POPUP-MODELL DER DIAGRAMMKACHELN (Spec „Kachelrahmen und Darstellungsarten“, K22–K24). Reine Funktionen: `chartOption`
 * baut die Option für `useDiagramm`, `popupLines`, `a11yName` und `a11yRows` den Text zur selben Antwort. Die Zeichenfläche kennt keine
 * einzelne Kachel, nur `TileData`.
 */

type Bucket = TileData["buckets"][number];
type Values = readonly (number | null)[];
type DateRange = { from: string; to: string };

export type ChartInput = { data: TileData; definition: TileDefinition; display: "SERIES" | "STACKED";
  companies: readonly Unternehmen[]; phone: boolean };

const DAY_MS = 86_400_000;
/**
 * `useDiagramm` mischt Optionen (`notMerge: false`): Eine entfallene Reihe bliebe stehen (Unternehmensfilter, Vergleich aus, SERIES nach
 * STACKED, gemessen an ECharts 6.1). Darum hat jede Option dieselbe Zahl Reihen in derselben Folge und jeder Platz dieselben Schlüssel;
 * freie Plätze sind leer. Acht sind die Plätze der Diagrammpalette.
 */
const SERIES_SLOTS = 8;
const STACK = "total";
const SUM_KEY = "total";
const BASE_Z = 2, COMPARE_Z = 5, NOW_Z = 6;
const COMPARE_HALO_PX = 2.5;
const AXIS_FONT_PX = 10;
/** Höchstens so viele Marken stehen an der Zeitachse. */
const AXIS_MARKS = { desktop: 12, phone: 6 };
/** Luft links für die Wertachse, oben für „jetzt“, unten für die Zeitachse. */
export const GRID = { left: 52, right: 8, top: 18, bottom: 18 };
/** Gemeinsam für Flächen und Vergleich: das Popup ist ein eigener Knoten, die Reihen selbst sind still. */
const STEP_LINE = { type: "line", step: "end", connectNulls: false, showSymbol: false, silent: true, clip: true, emphasis: { disabled: true } };

const toCents = (v: number): number => Math.round(v * 100) / 100;
const spanOf = (d: TileData): DateRange => d.range ?? { from: d.today, to: d.today };
const isBelowZero = (v: number | null): boolean => v !== null && v < 0;

/** Summe ohne `null`; nur `null` ergibt `null`, nie 0 (643). Centgenau wie der Server (K14). */
function sumOf(values: Values): number | null {
  const known = values.filter((v) => v !== null);
  return known.length === 0 ? null : toCents(known.reduce((a, b) => a + b, 0));
}

/** `amounts` und `compareValues` trägt nur eine Reihe wie beim Angebotserfolg (645), auch in einem Zeitraum ohne verlorenes Angebot. */
const hasOwnFigures = (s: TileSeries): boolean => s.amounts !== undefined || s.compareValues !== undefined;

/** Vorzeichenbehaftet (645): die Reihe liegt unter null oder ist von der Art des Angebotserfolgs. Sie ist nie Quelle einer Kennzahl. */
export const isSignedSeries = (s: TileSeries): boolean => hasOwnFigures(s) || s.values.some(isBelowZero);
const isSigned = (d: TileData): boolean => d.series.some(isSignedSeries);

/** Der Vergleich läuft je Reihe über `compareValues`, sonst ist er eine Linie aus `totals` (645). */
const comparesPerSeries = (d: TileData): boolean => d.compare !== null && d.series.some((s) => s.compareValues !== undefined);

// ── Treppen (K23) ─────────────────────────────────────────────────────────────────────────────────────────────

/**
 * Die Stützstellen einer Treppe für `step: "end"`: eine je Abschnittsanfang und der Schlusspunkt bei `to` + 1 Tag. Den Abschnitt vor einem
 * `null` schließt ECharts selbst, die Treppe nimmt das x des nächsten Punkts auch bei `null`. Alle Reihen tragen dieselben Stellen, denn
 * ECharts stapelt auf einer Zeitachse nach Index. Beides sichert `packages/ui/test/echartsVertrag.test.ts`.
 */
export function stepData(buckets: readonly Bucket[], values: Values, to: string): [number, number | null][] {
  return [...buckets.map((b, k): [number, number | null] => [dayMs(b.start), values[k] ?? null]),
    [dayMs(to) + DAY_MS, values[buckets.length - 1] ?? null]];
}

/** Anfang und Ende eines Abschnitts auf der Zeitachse: der letzte Tag zählt bis Mitternacht. */
export const bucketSpan = (b: Bucket): [number, number] => [dayMs(b.start), dayMs(b.end) + DAY_MS];

/** Der Abschnitt unter dem Zeiger, nicht die nächste Stützstelle (Spec „Popup“); −1 außerhalb. */
export function bucketAt(buckets: readonly Bucket[], ms: number): number {
  return buckets.findIndex((b) => {
    const [start, end] = bucketSpan(b);
    return ms >= start && ms < end;
  });
}

/** FLOW: Summe ohne `null`; STOCK: der letzte Wert ≠ `null`. */
export function seriesTotal(s: TileSeries): number | null {
  return s.measure === "STOCK" ? s.values.filter((v) => v !== null).at(-1) ?? null : sumOf(s.values);
}

/** SERIES zeichnet eine Summenreihe in ACCENT. Vorzeichenbehaftet bleibt je Reihe, eine Summe wäre ein sinnloser Saldo (645). */
export function displaySeries(data: TileData, display: "SERIES" | "STACKED"): TileSeries[] {
  if (display === "STACKED" || data.series.length < 2 || isSigned(data)) return data.series;
  return [{ key: SUM_KEY, label: T.popup.sum, color: { tone: "ACCENT" }, measure: data.series[0]!.measure,
    values: data.buckets.map((_, k) => sumOf(data.series.map((s) => s.values[k] ?? null))) }];
}

/** Die eine Abbildung für Diagramm und Legende (644, 649). Ein Unternehmen außerhalb der Liste wird neutral. */
export function tileColor(c: TileColor, thema: DiagrammThema, companies: readonly Unternehmen[]): string {
  if ("tone" in c) return toneFarbe(c.tone, thema);
  if ("palette" in c) return paletteFarbe(c.palette, thema);
  const company = companies.find((u) => u.id === c.companyId);
  return company === undefined ? toneFarbe("NEUTRAL", thema) : hausfarbeImDiagramm(company.colorHex, thema);
}

// ── Option ────────────────────────────────────────────────────────────────────────────────────────────────────

function compareLines(d: TileData): Values[] {
  if (d.compare === null) return [];
  return comparesPerSeries(d) ? d.series.map((s) => s.compareValues ?? []) : [d.compare.totals];
}

function axisText(day: string, grain: Grain | null, showYear: boolean): string {
  if (grain === "WEEK") return T.axis.week(isoWeek(day));
  if (grain !== "MONTH") return formatDatum(day).slice(0, 6);
  return showYear ? day.slice(0, 4) : T.period.monthsShort[Number(day.slice(5, 7)) - 1]!;
}

/**
 * Die Marken der Zeitachse, x → Text. Eine Marke braucht Platz für ihren Schritt, linksbündig liefe sie sonst rechts aus dem Bild. Über
 * mehrere Jahre nennt die erste Marke jedes Jahres das Jahr, auch wenn kein Januar auf eine Marke fällt.
 */
function axisMarks(buckets: readonly Bucket[], grain: Grain | null, phone: boolean): Map<number, string> {
  const step = Math.ceil(buckets.length / (phone ? AXIS_MARKS.phone : AXIS_MARKS.desktop));
  const multiYear = buckets[0]?.start.slice(0, 4) !== buckets.at(-1)?.end.slice(0, 4);
  let lastYear: string | null = null;
  return new Map(buckets.filter((_, k) => k % step === 0 && k + step <= buckets.length).map((b): [number, string] => {
    const year = b.start.slice(0, 4), showYear = multiYear && year !== lastYear;
    lastYear = year;
    return [dayMs(b.start), axisText(b.start, grain, showYear)];
  }));
}

/** Minuten zeichnet die Achse als Stunden, sonst wählt ECharts Stufen in Minuten („16,7 h“). Popup und Tabelle bleiben bei `valueText`. */
const onAxis = (values: Values, unit: TileUnit): Values => (unit === "MINUTES" ? values.map((v) => (v === null ? null : v / 60)) : values);

export function chartOption(i: ChartInput, thema: DiagrammThema): Record<string, unknown> {
  const { data, display, companies, phone } = i;
  const { from, to } = spanOf(data);
  const stacked = display === "STACKED";
  const shown = displaySeries(data, display);
  const lines = compareLines(data);
  const slots = Array.from({ length: Math.max(SERIES_SLOTS, companies.length, data.series.length) }, (_, k) => k);
  const unit = i.definition.unit;
  const marks = axisMarks(data.buckets, data.grain, phone);
  const hasToday = from <= data.today && data.today <= to;

  const base = (k: number): Record<string, unknown> => {
    const s = shown[k];
    const color = s === undefined ? thema.aus : tileColor(s.color, thema, companies);
    return { ...STEP_LINE, id: `base:${k}`, name: s?.label ?? "", z: BASE_Z, stack: stacked && s !== undefined ? STACK : null, stackStrategy: "samesign",
      data: s === undefined ? [] : stepData(data.buckets, onAxis(s.values, unit), to),
      // Im Stapel ohne Strich: Eine Reihe mit 0 läge sonst als Linie oben auf dem Stapel, „verloren“ stiege durch „gewonnen“ hinauf.
      lineStyle: { color, width: stacked ? 0 : 1.25 }, areaStyle: { color, opacity: stacked ? 0.8 : 0.24 } };
  };
  const compare = (k: number): Record<string, unknown> => {
    const values = lines[k];
    return { ...STEP_LINE, id: `compare:${k}`, z: COMPARE_Z, data: values === undefined ? [] : stepData(data.buckets, onAxis(values, unit), to),
      // Auf der dichten Fläche des Stapels trägt ein Saum in der Flächenfarbe den Strich, im dunklen Thema läge er sonst bei gut 1:1 (G3).
      lineStyle: { color: thema.leise, width: 1.5, type: "dashed", shadowColor: thema.flaeche, shadowBlur: stacked ? COMPARE_HALO_PX : 0 } };
  };
  const now = { id: "now", type: "line", data: [], silent: true,
    markLine: { silent: true, symbol: ["none", "none"], animation: false, emphasis: { disabled: true }, z: NOW_Z,
      lineStyle: { color: thema.jetzt, type: "solid", width: 1 },
      // Waagerecht neben dem oberen Ende der Linie: Ohne `rotate: 0` stünde die innere Beschriftung senkrecht. Sie endet an der Linie,
      // am rechten Rand wird also nichts abgeschnitten.
      label: { show: true, formatter: T.now, rotate: 0, position: "insideEndTop", color: thema.jetzt, fontSize: AXIS_FONT_PX },
      data: hasToday ? [{ xAxis: nowOnAxis(data.today, new Date(data.now)) }] : [] } };

  return {
    useUTC: true,
    grid: { ...GRID, containLabel: false, outerBoundsMode: "none" },
    xAxis: { type: "time", min: dayMs(from), max: dayMs(to) + DAY_MS, splitLine: { show: false }, axisTick: { show: false },
      // Bei Reihen unter null bleibt die Zeitachse unten, sonst stünde sie mitten im Bild.
      axisLine: { onZero: false, lineStyle: { color: thema.raster } },
      axisLabel: { customValues: [...marks.keys()], formatter: (ms: number) => marks.get(ms) ?? "",
        color: thema.leise, fontSize: AXIS_FONT_PX, align: "left", hideOverlap: true } },
    yAxis: { type: "value", splitNumber: 3, minInterval: unit === "COUNT" ? 1 : 0, axisLine: { show: false }, axisTick: { show: false },
      splitLine: { lineStyle: { color: thema.raster } },
      axisLabel: { formatter: (v: number) => T.axis.value(v, unit), color: thema.leise, fontSize: AXIS_FONT_PX } },
    dataZoom: [],
    tooltip: { show: false },
    series: [...slots.map(base), ...slots.map(compare), now],
  };
}

// ── Popup und Barrierefreiheit ────────────────────────────────────────────────────────────────────────────────

function sectionText(b: Bucket, grain: Grain | null): string {
  const range = rangeText({ from: b.start, to: b.end });
  if (grain === "WEEK") return T.popup.week(isoWeek(b.start), range, b.partial);
  if (grain === "MONTH") return T.popup.month(Number(b.start.slice(5, 7)), b.start.slice(0, 4), range, b.partial);
  return formatDatum(b.start);
}

/** Eine Summe gibt es bei mehreren Reihen ohne Vorzeichen, sonst wäre sie ein Saldo (645). */
const hasSumLine = (d: TileData): boolean => d.series.length > 1 && !isSigned(d);

const sumAt = (d: TileData, k: number): number | null => sumOf(d.series.map((s) => s.values[k] ?? null));

/** „Δ +14.300 € (+13 %)“: Basis − Vergleich, Prozent nur bei einem Vergleichswert ungleich 0; ohne beide Zahlen `null`. */
export function deltaText(current: number | null, previous: number | null, unit: TileUnit, currency: string | null): string | null {
  if (current === null || previous === null) return null;
  const d = toCents(current - previous);
  const percent = previous === 0 ? null : (d / Math.abs(previous)) * 100;
  return T.popup.delta(T.popup.signed(d, T.valueText(Math.abs(d), unit, currency)),
    percent === null ? null : T.popup.signed(percent, T.valueText(Math.abs(percent), "PERCENT", null)));
}

/** Die Tönung eines Δ nach der guten Richtung der Kachel; gleich oder ohne Richtung bleibt es neutral. */
export const deltaTone = (d: number, better: TileDefinition["better"]): "gut" | "schlecht" | null =>
  (better === null || d === 0 ? null : (d > 0) === (better === "UP") ? "gut" : "schlecht");

/** Die Texte einer Antwort, aus denen Popup und Tabelle ihre Zellen bauen. */
function cellsOf({ data, definition }: ChartInput) {
  const money = (cents: number): string => T.valueText(Math.abs(cents) / 100, "MONEY", data.currency);
  const value = (v: number | null): string => (v === null ? T.popup.noValue : T.valueText(v, definition.unit, data.currency));
  /** Der Wert einer Reihe, mit Betrag aus `amounts`, wo es einen gibt. „verloren“ steht ohne Vorzeichen, die Lage im Diagramm trägt es; eine Korrektur behält ihres. */
  const seriesCell = (s: TileSeries, v: number | null, cents: number | undefined): string => {
    const text = value(v !== null && hasOwnFigures(s) ? Math.abs(v) : v);
    return v === null || cents === undefined || cents === 0 ? text : T.popup.withAmount(text, money(cents));
  };
  const delta = (current: number | null, previous: number | null): string | null => deltaText(current, previous, definition.unit, data.currency);
  return { value, seriesCell, delta };
}

/** Eine Popup-Zeile: `label` „ME: “, `value` „60.000 €“ für die rechte Spalte; Abschnitt, Kopfzeile des Vergleichs und Δ haben keinen Wert. */
export type PopupRow = { label: string; value: string | null };

/**
 * Das Popup als Zeilen in fester Folge: Abschnitt, je Reihe in der Folge von `data.series` ein Wert, die Summe (nur bei mehreren Reihen
 * und ohne Vorzeichen), dann der Vergleich. Vorzeichenbehaftet folgt „Vorjahr April 2025“ und je Reihe ihr Vergleichswert, ohne Saldo
 * und ohne Δ (645); sonst „Vorjahr April 2025: 80.000 €“ und, wenn Basis und Vergleich Zahlen sind, das Δ.
 */
export function popupRows(i: ChartInput, index: number): PopupRow[] {
  const { data } = i;
  const bucket = data.buckets[index];
  if (bucket === undefined) return [];
  const { value, seriesCell, delta } = cellsOf(i);
  const row = (label: string, cell: string): PopupRow => ({ label: T.popup.line(label, ""), value: cell });
  const rows: PopupRow[] = [{ label: sectionText(bucket, data.grain), value: null },
    ...data.series.map((s) => row(s.label, seriesCell(s, s.values[index] ?? null, s.amounts?.[index])))];
  if (hasSumLine(data)) rows.push(row(T.popup.sum, value(sumAt(data, index))));
  const c = data.compare;
  if (c === null) return rows;
  const other = c.buckets[index];
  const name = T.popup.compareSection(c.mode, other === undefined ? null : sectionText(other, data.grain));
  if (comparesPerSeries(data)) {
    return [...rows, { label: name, value: null }, ...data.series.map((s) => row(s.label, seriesCell(s, s.compareValues?.[index] ?? null, s.compareAmounts?.[index])))];
  }
  const previous = c.totals[index] ?? null;
  rows.push(row(name, value(previous)));
  const change = delta(sumAt(data, index), previous);
  return change === null ? rows : [...rows, { label: change, value: null }];
}

/** Dieselben Zeilen als Text: „ME: 60.000 €“. */
export const popupLines = (i: ChartInput, index: number): string[] => popupRows(i, index).map((r) => r.label + (r.value ?? ""));

/** Das Δ der letzten Popup-Zeile als Zahl, sonst `null` (kein Vergleich, Vergleich je Reihe, eine Seite ohne Wert): Die Oberfläche tönt die Zeile danach. */
export function popupDelta({ data }: ChartInput, index: number): number | null {
  const current = sumAt(data, index), previous = data.compare?.totals[index] ?? null;
  return comparesPerSeries(data) || current === null || previous === null ? null : toCents(current - previous);
}

/** „2026“, „Q4 2026“, „Oktober 2026“, sonst die Spanne: `ChartInput` trägt keine `Period` (Nachtrag nach Aufgabe 8). */
function rangeName(r: DateRange): string {
  const year = r.from.slice(0, 4);
  const month = Number(r.from.slice(5, 7)), lastMonth = Number(r.to.slice(5, 7));
  const wholeMonths = year === r.to.slice(0, 4) && r.from.endsWith("-01") && new Date(dayMs(r.to) + DAY_MS).getUTCDate() === 1;
  if (wholeMonths && month === 1 && lastMonth === 12) return year;
  if (wholeMonths && month % 3 === 1 && lastMonth === month + 2) return T.period.quarterName((month + 2) / 3, year);
  if (wholeMonths && lastMonth === month) return T.period.monthName(T.period.months[month - 1]!, year);
  return rangeText(r);
}

/** „Umsatz netto 2026: 812.400 €, Vorjahr 765.000 €“: die erste Kennzahl mit Vergleich, sonst die erste. Ohne Kennzahlen die Summe der Reihen. */
export function a11yName(i: ChartInput): string {
  const { data, definition } = i;
  const { value } = cellsOf(i);
  const name = rangeName(spanOf(data));
  const count = data.counts.find((c) => c.compareValue !== null) ?? data.counts[0];
  if (count === undefined) {
    const total = sumOf(displaySeries(data, "SERIES").map(seriesTotal));
    return T.a11y.summary(definition.title, name, value(total), null);
  }
  const text = T.valueText(count.value, count.unit, data.currency);
  const comparison = data.compare === null || count.compareValue === null ? null
    : T.a11y.comparison(T.popup.compareModes[data.compare.mode], T.valueText(count.compareValue, count.unit, data.currency));
  return T.a11y.summary(definition.title, name, count.label === definition.title ? text : T.a11y.comparison(count.label, text), comparison);
}

/** Die unsichtbare Tabelle: Zeile 0 ist der Kopf, danach ein Abschnitt je Zeile, Spalten wie das Popup. */
export function a11yRows(i: ChartInput): string[][] {
  const { data } = i;
  const { value, seriesCell, delta } = cellsOf(i);
  const H = T.a11y.tableHead;
  const hasSum = hasSumLine(data);
  const perSeries = comparesPerSeries(data);
  const hasTotal = data.compare !== null && !perSeries;
  const head = [H.bucket, ...data.series.map((s) => s.label), ...(hasSum ? [H.sum] : []),
    ...(perSeries ? data.series.map((s) => T.a11y.comparison(H.compare, s.label)) : []), ...(hasTotal ? [H.compare, H.delta] : [])];
  return [head, ...data.buckets.map((b, k) => {
    const previous = data.compare?.totals[k] ?? null;
    return [sectionText(b, data.grain), ...data.series.map((s) => seriesCell(s, s.values[k] ?? null, s.amounts?.[k])),
      ...(hasSum ? [value(sumAt(data, k))] : []),
      ...(perSeries ? data.series.map((s) => seriesCell(s, s.compareValues?.[k] ?? null, s.compareAmounts?.[k])) : []),
      ...(hasTotal ? [value(previous), delta(sumAt(data, k), previous) ?? T.popup.noValue] : [])];
  })];
}
